import { CommonModule } from '@angular/common';
import { Component, Input, OnDestroy, ViewChild } from '@angular/core';
import {
  FormBuilder,
  FormGroup,
  FormsModule,
  ReactiveFormsModule,
} from '@angular/forms';
import { Observable, of } from 'rxjs';
import { VehicleMetadata } from 'src/app/_types/lead.types';
import { InsuranceType } from 'src/app/enums/insurance-type.enum';

import { LeadFormBase } from '../../lead-form-base';
import { VehicleFormComponent } from '../../vehicle-form/vehicle-form.component';

@Component({
  selector: 'app-business-vehicle-form',
  templateUrl: './business-vehicle-form.component.html',
  styleUrls: ['./business-vehicle-form.component.css'],
  standalone: true,
  imports: [
    CommonModule,
    FormsModule,
    ReactiveFormsModule,
    VehicleFormComponent,
  ],
})
export class BusinessVehicleFormComponent
  extends LeadFormBase
  implements OnDestroy
{
  @Input()
  formGroup!: FormGroup;

  @ViewChild('vehicleForm') vehicleForm!: VehicleFormComponent;

  constructor(private fb: FormBuilder) {
    super();
  }

  sharedBusinessFormGroup: FormGroup = this.fb.group({
    activityArea: [null],
    activityDescription: [null],
    hasLaborAgreement: [null],
    laborAgreement: [null],
    isInternational: [null],
    activityCountries: [null],
    hasSubcontractors: [null],
    collaboratorsCount: [null],
    yearlyAnnualRevenue: [null],
    yearlySalaries: [null],
  });

  vehicleFormGroup: FormGroup = this.fb.group({
    metadata: this.fb.group({
      type: InsuranceType.BUSINESS_MOTOR_VEHICLE,
    }),
  });

  metadataFormGroup: FormGroup = this.fb.group({
    type: InsuranceType.BUSINESS_MOTOR_VEHICLE,
    sharedBusiness: this.sharedBusinessFormGroup,
    vehicleMetadata: this.vehicleFormGroup.get('metadata'),
  });

  override ngOnInit(): void {
    super.ngOnInit();
    this.formGroup.removeControl('metadata');

    if (this.metadata) {
      // Cast to business vehicle metadata type to access business-specific properties
      const vehicleMetadata = this.metadata as unknown as VehicleMetadata;

      this.vehicleFormGroup.patchValue({ metadata: vehicleMetadata });
    }

    this.formGroup.addControl('metadata', this.metadataFormGroup);
  }

  ngOnDestroy(): void {
    // Clean up any subscriptions if needed
  }

  override handleSubmit(): Observable<boolean> {
    // First handle the vehicle form submission
    if (this.vehicleForm) {
      this.vehicleForm.handleSubmit().subscribe();
    }

    // Merge shared business form data and vehicle data into the main metadata form
    const sharedData = this.sharedBusinessFormGroup.value;
    const vehicleData = this.vehicleFormGroup.get('metadata')?.value || {};

    // Set the correct business vehicle type
    vehicleData.type = InsuranceType.BUSINESS_MOTOR_VEHICLE;

    // Merge all data
    const mergedData = { ...vehicleData, ...sharedData };

    // Update the metadata form group with merged data
    Object.keys(mergedData).forEach((key) => {
      if (this.metadataFormGroup.controls[key]) {
        this.metadataFormGroup.controls[key].setValue(mergedData[key]);
      } else {
        this.metadataFormGroup.addControl(
          key,
          this.fb.control(mergedData[key])
        );
      }
    });

    // Clean up nested form groups
    this.metadataFormGroup.removeControl('sharedBusiness');
    this.metadataFormGroup.removeControl('vehicleMetadata');

    return of(true);
  }
}
